<template>
    <div class="w-full max-w-120 rounded shadow-xl | transition duration-300">
        <form class="p-6 rounded-t bg-white space-y-6" @submit.prevent="handleLogin">
            <div class="space-y-3">
                <nio-input
                    :placeholder="$t('auth.login.email')"
                    required
                    type="email"
                    v-model="email"
                />
                <nio-input
                    :placeholder="$t('auth.login.password')"
                    required
                    type="password"
                    v-model="password"
                />
            </div>
            <nio-button
                class="w-full"
                color="blue"
                :loading="loading"
                type="submit"
            >
                {{ $t('auth.login.connect') }}
            </nio-button>

            <nio-alert type="error" v-if="error">
                {{ $t('auth.login.failed') }}
            </nio-alert>
        </form>

        <div class="flex justify-between items-center p-6 space-x-6 border-t border-gray-200 rounded-b bg-gray-100">
            <div class="flex flex-wrap items-center">
                <nio-icon class="mr-2" name="regular/earth-1" />
                <language-chooser :locales="locales" :value="$i18n.locale" @input="setLocale" />
            </div>

            <div class="flex items-center">
                <nio-icon class="mr-2" name="regular/login-key" />
                <router-link :to="{ name: 'auth.forgotPassword' }">
                    {{ $t('auth.login.forgotPassword') }}
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import LanguageChooser from '@/components/LanguageChooser.vue';

    export default {
        components: {
            LanguageChooser,
        },

        props: {
            baseEmail: {
                type: String,
                default: '',
            },
        },

        data() {
            return {
                email: '',
                password: '',
                error: false,
                loading: false,
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            locales() {
                return [
                    { value: 'fr', label: 'Français' },
                    { value: 'en', label: 'English' },
                ];
            },
        },

        watch: {
            email() {
                this.error = false;
            },

            password() {
                this.error = false;
            },
        },

        methods: {
            ...mapActions([
                'fetchUser',
                'setAuthUser',
            ]),

            setLocale(locale) {
                this.$i18n.locale = locale;
            },

            handleLogin() {
                this.loading = true;

                const payload = {
                    grant_type: 'password',
                    client_id: process.env.VUE_APP_API_CLIENT_ID,
                    client_secret: process.env.VUE_APP_API_CLIENT_SECRET,
                    username: this.email,
                    password: this.password,
                    provider: 'users',
                    scope: '*',
                };

                this.$axios.post('oauth/token', payload)
                    .then(response => this.onSuccess(response.data.access_token))
                    .catch(() => this.handleActivixLogin(payload));
            },

            handleActivixLogin(payload) {
                payload.client_id = process.env.VUE_APP_CRM_API_CLIENT_ID;
                payload.client_secret = process.env.VUE_APP_CRM_API_CLIENT_SECRET;

                this.$axios.post(`${process.env.VUE_APP_CRM_API_URL}/oauth/token`, payload)
                    .then(response => this.fetchActivixUser(response.data.access_token))
                    .catch(() => this.onError());
            },

            fetchActivixUser(token) {
                this.$axios.get(`${process.env.VUE_APP_CRM_API_URL}/v1/auth-user`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                    .then(response => this.onSuccess(response.data.data.niotext_token))
                    .catch(() => this.onError());
            },

            onSuccess(token) {
                const authUser = {
                    access_token: token,
                };

                this.setAuthUser(authUser);
                this.fetchUser(true)
                    .then(() => {
                        this.$router.push({ name: 'chat' });
                    });
            },

            onError() {
                this.password = '';
                this.loading = false;

                this.$nextTick(() => {
                    this.error = true;
                });
            },
        },

        beforeRouteEnter(to, from, next) {
            next(vm => {
                if (vm.authUser && vm.authUser.access_token) {
                    vm.$router.replace({ name: 'chat' });
                    return;
                }

                next();
            });
        },

        created() {
            this.email = this.baseEmail;
        },
    };
</script>
